// pages/events/index.js
import {
  fetchEventByIdServer,
  fetchEventsByVenueIdServer,
  fetchVenueByIdServer,
} from "../../hooks";
import { useRouter } from "next/router";
import EventsLayout from "@/components/Core/Events";
import Head from "next/head"; // Import Head
import { Timestamp, GeoPoint } from "firebase/firestore";
import CustomDrawer from "@/components/Elements/CustomDrawer";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import SocialMediaLinks from "../../components/SocialMediaLinks";
import Link from "next/link";
import {
  Attribute,
  AttributeLabel,
  AttributeValue,
} from "../../components/Attribute";
import { getAttributeValue } from "../../utils/utility";
import { useAppState } from "../../appContext";
import MyGoogleMap from "../../components/Elements/GoogleMap";
import format from "date-fns/format";
import AddCalendar from "@/components/Elements/AddCalendar";

function EventsPage(props) {
  const router = useRouter();
  const { eventId, from } = router.query;
  const backHref = from || "/events";
  const backLabel = from?.startsWith("/venues/")
    ? "Back to Venue"
    : from?.startsWith("/bands/")
      ? "Back to Band"
      : "Back to Events";
  const [state] = useAppState();
  const attributes = [
    {
      label: "Title",
      name: "title",
      sortable: true,
    },
    {
      label: "Description",
      name: "eventdescription",
      component: {
        type: "html",
      },
    },
    {
      label: "Event Date",
      name: "main_date",
      sortable: true,
      component: {
        type: "dateTime",
      },
    },
    {
      label: "Added Date",
      name: "modifieddate",
      sortable: true,
      component: {
        type: "dateTime",
      },
    },
    {
      label: "Event Type",
      name: "eventtype",
    },
    {
      label: "Buy Tickets",
      name: "ticket_url",
    },
  ];

  const handleClose = () => {
    router.push(from || "/events");
  };

  const onDrawerClose = () => {
    const query = { ...router.query };
    delete query.eventId;
    router.push({
      pathname: router.pathname,
      query: query,
    });
  };
  function calculateEndDate(startDateString) {
    if (!startDateString) {
      return undefined; // Or handle the case where startDate is missing
    }

    const startDate = new Date(startDateString);
    const endDate = new Date(startDate.getTime() + 4 * 60 * 60 * 1000); // Add 4 hours

    return endDate.toISOString(); // Format as ISO string
  }

  const eventSchema = props.event && {
    "@context": "http://schema.org",
    "@type": "Event",
    name: props.event.title,
    startDate: props.event.main_date,
    endDate: props.event.endDate || calculateEndDate(props.event.main_date),
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    location: {
      "@type": "Place",
      name: props.eventVenue?.venue_name,
      address: {
        "@type": "PostalAddress",
        addressLocality: props.eventVenue?.venue_city,
        addressRegion: props.eventVenue?.venue_state,
        postalCode: props.eventVenue?.VENUE_ZIP || "",
        addressCountry: "US",
      },
    },
    image: [
      props.event.imageUrl || "https://webtunes.com/images/1x1/webtunes.jpg",
      props.event.imageAlt || "https://webtunes.com/images/4x3/webtunes.jpg",
      props.event.imageBanner ||
        "https://webtunes.com/images/16x9/webtunes.jpg",
    ],
    description: props.event.description || props.event.title,
    offers: {
      "@type": "Offer",
      url: "https://webtunes.com",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      validFrom: props.event.main_date,
    },
    performer:
      props.event.bands && props.event.bands.length > 0
        ? {
            "@type": "Person",
            name:
              props.event.bands[0].bandname ||
              props.event.title ||
              "Unknown Performer",
            url: props.event.bands[0].internallink
              ? `https://webtunes.com/bands/${props.event.bands[0].internallink}`
              : "https://webtunes.com", // Provide a fallback URL
          }
        : {
            "@type": "Person",
            name: props.event.title || "Unknown Performer", // Fallback if no bands
            url: "https://webtunes.com", // Fallback URL
          },
    organizer: {
      "@type": "Organization",
      name: props.eventVenue?.venue_name,
      url: "https://webtunes.com",
    },
  };
  if (eventId) {
    if (!props.event || !props.eventVenue) return <p>Loading...</p>;
    const readableDate = format(
      new Date(props.event?.main_date),
      "MMMM d, yyyy 'at' h:mm a"
    );
    return (
      <>
        <Head>
          <title>{`${props.event?.title} at ${props.event?.venue_name} on ${readableDate}`}</title>
          <meta
            name="description"
            content={`${props.event?.title} at ${props.event?.venue_name} on ${props.event?.main_date}`}
          />
          <meta
            name="keywords"
            content={`${props.event?.title} at ${props.event?.venue_name}, Live Music, ${props.event?.main_date}`}
          />
          <meta
            property="og:title"
            content={`${props.event?.title} at ${props.event?.venue_name} on ${props.event?.main_date}`}
          />
          <meta
            property="og:description"
            content={`${props.event?.title} at ${props.event?.venue_name} on ${props.event?.main_date}`}
          />
        </Head>
        {/* JSON-LD schema.org structured data */}
        {eventSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(eventSchema) }}
          />
        )}
        <CustomDrawer
          open={true}
          eventId={props.event.eventId}
          //          loading={loading}
          onClose={handleClose}
          title={props.event?.title}
        >
          {props.event ? (
            <>
              <Box
                sx={{
                  padding: 2,
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    mb: 5,
                    height: props.event?.imageUrl ? "250px" : "0",
                    overflow: "hidden",
                  }}
                >
                  {props.event?.imageUrl && (
                    <Box
                      component="img"
                      src={
                        props.event.imageUrl
                          ? props.event.imageUrl
                          : "https://via.placeholder.com/1100x250?text=Cover+Photo"
                      }
                      alt="Event Photo"
                      sx={{
                        objectFit: "cover",
                      }}
                    />
                  )}
                </Box>
                {/* <div>
                  <ins
                    className="adsbygoogle"
                    style={{
                      display: "block",
                      width: "300px",
                      height: "250px",
                    }}
                    data-ad-client="ca-pub-6596539204451830"
                    data-ad-slot="8925057038"
                  ></ins>
                  <script
                    id="adsbygoogle-init"
                    strategy="afterInteractive"
                    dangerouslySetInnerHTML={{
                      __html: `(adsbygoogle = window.adsbygoogle || []).push({});`,
                    }}
                  />
                </div> */}
                <Link href={backHref}>
                  <Button
                    sx={{
                      backgroundColor: "#334e65",
                      color: "white",
                      mb: 2,
                      textTransform: "none",
                    }}
                  >
                    ← {backLabel}
                  </Button>
                </Link>
                <h4 style={{ textDecoration: "underline", fontWeight: "bold" }}>
                  Event Details
                </h4>
                {attributes.map((attribute, idx) => (
                  <Attribute key={idx + attribute.name}>
                    <AttributeLabel>{attribute.label}</AttributeLabel>
                    <AttributeValue>
                      {attribute.name === "ticket_url" &&
                      props.event[attribute.name] ? (
                        <a
                          href={props.event[attribute.name]}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {props.event[attribute.name]}
                        </a>
                      ) : attribute?.component ? (
                        getAttributeValue(attribute, props.event, state)
                      ) : (
                        props.event[attribute.name] || "-"
                      )}
                    </AttributeValue>
                  </Attribute>
                ))}

                <AddCalendar event={props.event} />
                {props.event?.bands?.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography textAlign="left" sx={{ fontWeight: "bold" }}>
                      Bands Playing:
                    </Typography>
                    {props.event?.sampleVideoUrl && (
                      <Box sx={{ my: 4 }}>
                        <Typography variant="h6" gutterBottom>
                          Video
                        </Typography>
                        <iframe
                          width="100%"
                          height="315"
                          src={props.event.sampleVideoUrl.replace(
                            "watch?v=",
                            "embed/"
                          )}
                          title="Sample Video"
                          allowFullScreen
                        />
                      </Box>
                    )}
                    {props.event.bands.map((band, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          textAlign: "center",
                          width: "150px", // Adjust the size as needed
                        }}
                      >
                        <a
                          href={`/bands${band.internallink || ""}`} // Dynamically link to the band's page
                          style={{ textDecoration: "none", color: "inherit" }} // Styling for the link
                        >
                          <img
                            src={band.imageUrl || "/default-band-image.jpg"}
                            alt={band.bandname || "Unknown Band"}
                            style={{
                              width: "100%",
                              height: "150px", // Adjust size
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                            onError={(e) => {
                              e.target.onerror = null; // Prevent infinite loop
                              e.target.src = "/default-band-image.jpg"; // Replace with default image
                            }}
                          />
                          <Typography sx={{ mt: 1 }}>
                            {band.bandname || "Unknown Band"}
                          </Typography>
                        </a>
                      </Box>
                    ))}
                  </>
                )}
                <Divider sx={{ my: 2 }} />
                <h4 style={{ textDecoration: "underline", fontWeight: "bold" }}>
                  Event Location
                </h4>
                <VenueComponent
                  venue={props.eventVenue}
                  loading={props.venueLoading}
                />
              </Box>
            </>
          ) : (
            <CircularProgress />
          )}
          <Divider />
          <Box sx={{ padding: 2 }}>
            <SocialMediaLinks links={props.event?.socialLinks} />
          </Box>
        </CustomDrawer>
      </>
    );
  } else {
    return (
      <>
        <Head>
          <title>Live Music Events | Webtunes</title>
          <meta
            name="description"
            content="Browse all upcoming live music events by date, location, venue, or band. Updated daily on Webtunes."
          />
          <meta
            name="keywords"
            content="Live music, Concerts, Events, Local bands, Venues, Webtunes"
          />
          <meta property="og:title" content="Live Music Events | Webtunes" />
          <meta
            property="og:description"
            content="Discover live music events happening near you. Filter by city, venue, or artist."
          />
          <link rel="canonical" href="https://webtunes.com/events" />
        </Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name: "Live Music Events",
              description: "A curated list of live music events near you.",
              url: "https://webtunes.com/events",
            }),
          }}
        />
        <div>
          <EventsLayout />
        </div>
      </>
    );
  }
}

const VenueComponent = ({ venue, loading = false }) => {
  return (
    <>
      {loading ? (
        <>
          <Skeleton height={50} animation="wave" />
          <Skeleton height={50} animation="wave" />
          <Skeleton height={50} animation="wave" />
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 2,
            flexDirection: "column",
          }}
        >
          <Box>
            <h3>
              <a href={`/venues${venue?.internallink}`}>{venue?.venue_name}</a>
            </h3>
          </Box>
          {venue?.lat && venue?.lon && (
            <Box sx={{ width: "100%" }}>
              <MyGoogleMap markers={[venue]} />
            </Box>
          )}
        </Box>
      )}
    </>
  );
};
export async function getServerSideProps({ query }) {
  const { eventId, venueId } = query;

  if (eventId) {
    try {
      const event = await fetchEventByIdServer(eventId);
      const eventVenue = await fetchVenueByIdServer(event.venue);

      if (!event || !eventVenue) {
        return {
          notFound: true,
        };
      }

      // Convert main_date to ISO string
      if (
        event.main_date instanceof Timestamp ||
        event.main_date instanceof Date
      ) {
        event.main_date = event.main_date.toDate().toISOString();
      }

      // Convert modifieddate to ISO string
      if (event.modifieddate instanceof Timestamp) {
        event.modifieddate = event.modifieddate.toDate().toISOString();
      }

      // Convert GeoPoint to plain object
      if (event.g && event.g.geopoint instanceof GeoPoint) {
        event.g.geopoint = {
          latitude: event.g.geopoint.latitude,
          longitude: event.g.geopoint.longitude,
        };
      }

      // Convert coordinates to plain object
      if (event.coordinates instanceof GeoPoint) {
        event.coordinates = {
          latitude: event.coordinates.latitude,
          longitude: event.coordinates.longitude,
        };
      }

      // Convert eventVenue coordinates to plain object
      if (eventVenue.coordinates instanceof GeoPoint) {
        eventVenue.coordinates = {
          latitude: eventVenue.coordinates.latitude,
          longitude: eventVenue.coordinates.longitude,
        };
      }

      // convert eventVenue GeoPoint
      if (eventVenue.g && eventVenue.g.geopoint instanceof GeoPoint) {
        eventVenue.g.geopoint = {
          latitude: eventVenue.g.geopoint.latitude,
          longitude: eventVenue.g.geopoint.longitude,
        };
      }

      return {
        props: { event, eventVenue },
      };
    } catch (error) {
      console.error("getServerSideProps error:", error);
      return {
        notFound: true,
      };
    }
  } else if (venueId) {
    try {
      const eventsByVenue = await fetchEventsByVenueIdServer(venueId, false);
      return {
        props: { eventsByVenue },
      };
    } catch (error) {
      console.error("Error fetching events by venue:", error);
      return {
        props: { eventsByVenue: [] },
      };
    }
  } else {
    return { props: {} };
  }
}

export default EventsPage;
